import React from "react";
import { FormControl, IFormControlProps } from "common/components/Form/FormControl";
import { Checkbox, ICheckboxProps } from "common/components/Checkbox";
import { DatePickerProps } from "common/components/DatePicker";
import { Dropdown, IDropdownProps } from "common/components/Dropdown";
import { IQueryableDropdownProps, QueryableDropdown } from "common/components/Dropdown/QueryableDropdown";
import { IRadioProps, Radio } from "common/components/Radio";
import { CheckboxGroup, ICheckboxGroupProps } from "common/components/CheckboxGroup";
import { ToggleListTable, ToggleListTableProps } from "common/components/Form/ToggleListTable";
import { CodeEditorProps } from "common/components/Form/CodeEditor";
import { Toggle, ToggleProps } from "common/components/Toggle";
import { SpinnerOverlayOnly } from "common/components/Spinner";

const CodeEditor = React.lazy(() =>
  import("common/components/Form/CodeEditor").then((module) => ({
    default: module.CodeEditor,
  })),
);

const DatePicker = React.lazy(() =>
  import("common/components/DatePicker").then((module) => ({
    default: module.DatePicker,
  })),
);

// Add new form fields here
export type FieldProps =
  | IFormControlProps
  | ICheckboxProps
  | ICheckboxGroupProps
  | IRadioProps
  | IDropdownProps
  | IQueryableDropdownProps
  | DatePickerProps
  | ToggleListTableProps
  | CodeEditorProps
  | ToggleProps;

export interface IFieldBaseProps<T extends string = FieldProps["type"]> {
  /** The input element id. If undefined, "name" will be used */
  id?: string;
  /** Text to display in the field label */
  label?: string;
  /** HTML name of the form element. This should match the record's key name. */
  name: string;
  /** If the input field should be disabled */
  disabled?: boolean;
  /** Field type */
  type: T;
}

export const Field: React.FC<FieldProps> = (props) => {
  switch (props.type) {
    case "simpleDropdown":
    case "multiDropdown":
      return <Dropdown {...props} />;
    case "queryableDropdown":
    case "queryableMultiDropdown":
      return <QueryableDropdown {...props} />;
    case "radio":
      return <Radio {...props} />;
    case "checkbox":
      return <Checkbox {...props} />;
    case "checkboxGroup":
      return <CheckboxGroup {...props} />;
    case "toggle":
      return <Toggle {...props} />;
    case "toggleListTable":
      return <ToggleListTable {...props} />;
    case "inputNumber":
    case "textarea":
    case "inputText":
    case "range":
      return <FormControl {...props} />;
    case "datepicker":
      return (
        <React.Suspense fallback={<SpinnerOverlayOnly />}>
          <DatePicker {...props} />
        </React.Suspense>
      );
    case "codeEditor":
      return (
        <React.Suspense fallback={<SpinnerOverlayOnly />}>
          <CodeEditor {...props} />
        </React.Suspense>
      );
    default:
      return null;
  }
};
